import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, AlertTriangle, Minus, Download, ClipboardCheck, FileDown } from "lucide-react";
import { AdminLayout } from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { TALENT_MAP } from "@/constants/testIds";
import { toast } from "sonner";
import { EvaluationDialog } from "./employee-detail/EvaluationDialog";

const FLAG_STYLE = {
  retener: { label: "Talento a retener", icon: Star, className: "text-emerald-700 bg-emerald-50" },
  revisar: { label: "Revisar antes de decidir", icon: AlertTriangle, className: "text-terracotta bg-terracotta/10" },
  neutral: { label: "Sin señales fuertes", icon: Minus, className: "text-charcoal/50 bg-accent" },
};

const FILTERS = [
  { value: "all", label: "Todos" },
  { value: "retener", label: "Talento a retener" },
  { value: "revisar", label: "Revisar antes de decidir" },
  { value: "neutral", label: "Sin señales fuertes" },
];

const EVALUATION_CADENCE_OPTIONS = [
  { value: "mensual", label: "Mensual" },
  { value: "trimestral", label: "Trimestral" },
  { value: "semestral", label: "Semestral" },
  { value: "anual", label: "Anual" },
];

function exportCsv(employees) {
  const header = ["Nombre", "Puesto", "Años de antigüedad", "Categoría de antigüedad", "Incidencias", "Salario mensual", "Clasificación"];
  const rows = employees.map((e) => [
    e.full_name, e.position, e.years_of_service, e.tenure_tier, e.incidents_count,
    e.current_salary ?? "Sin registrar", FLAG_STYLE[e.flag].label,
  ]);
  const csv = [header, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "mapa_talento_critico.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

export default function TalentMap() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [evaluationCadence, setEvaluationCadence] = useState("");
  const [savingCadence, setSavingCadence] = useState(false);
  const [evaluatingEmployee, setEvaluatingEmployee] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const loadTalentMap = () => api.get("/employees/talent-map").then(({ data }) => setData(data));

  useEffect(() => {
    loadTalentMap();
    api.get("/onboarding/status").then(({ data }) => setEvaluationCadence(data.evaluation_cadence || ""));
  }, []);

  const downloadEvaluationPdf = async (employee) => {
    if (!employee.last_evaluation) return;
    setDownloadingId(employee.id);
    try {
      const { data } = await api.get(`/employees/${employee.id}/evaluations/${employee.last_evaluation.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `evaluacion_${employee.full_name.replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo descargar el PDF de la evaluación");
    } finally {
      setDownloadingId(null);
    }
  };

  const saveEvaluationCadence = async (value) => {
    setEvaluationCadence(value);
    setSavingCadence(true);
    try {
      await api.patch("/onboarding/settings", { evaluation_cadence: value });
      toast.success("Periodicidad de evaluaciones actualizada");
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "No se pudo actualizar la periodicidad de evaluaciones");
    } finally {
      setSavingCadence(false);
    }
  };

  const filtered = data?.employees.filter((e) => filter === "all" || e.flag === filter) || [];

  return (
    <AdminLayout>
      <div className="max-w-4xl">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-3xl font-bold text-charcoal tracking-tight">Mapa de Talento Crítico</h1>
            <p className="text-charcoal/60 mt-1">{data?.note || "Cargando..."}</p>
          </div>
          {data && (
            <Button data-testid={TALENT_MAP.exportCsvButton} variant="outline" className="rounded-full" onClick={() => exportCsv(filtered)}>
              <Download className="w-4 h-4 mr-2" /> Exportar CSV
            </Button>
          )}
        </div>

        {data && (
          <div className="flex flex-wrap gap-2 mt-5">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                data-testid={`${TALENT_MAP.filterButton}-${f.value}`}
                onClick={() => setFilter(f.value)}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                  filter === f.value ? "bg-petroleum text-white border-petroleum" : "bg-card text-charcoal/70 border-petroleum/20 hover:bg-accent"
                }`}
              >
                {f.label} ({f.value === "all" ? data.employees.length : data.employees.filter((e) => e.flag === f.value).length})
              </button>
            ))}
          </div>
        )}

        <div className="space-y-3 mt-6">
          {filtered.map((e) => {
            const flag = FLAG_STYLE[e.flag];
            return (
              <Link key={e.id} to={`/admin/empleados/${e.id}`} data-testid={TALENT_MAP.employeeRow} className="block bg-card border border-petroleum/10 rounded-xl p-5 hover:-translate-y-0.5 hover:shadow-lg transition-all">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="font-semibold text-charcoal">{e.full_name}</p>
                    <p className="text-sm text-charcoal/60">{e.position} · {e.years_of_service} año(s) de antigüedad ({e.tenure_tier}) · {e.incidents_count} incidencia(s)</p>
                  </div>
                  <span data-testid={TALENT_MAP.flagBadge} className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full shrink-0 ${flag.className}`}>
                    <flag.icon className="w-3 h-3" /> {flag.label}
                  </span>
                </div>
              </Link>
            );
          })}
          {data && filtered.length === 0 && <p className="text-charcoal/50 text-sm">Sin empleados en esta categoría.</p>}
        </div>

        <h2 className="font-heading text-xl font-semibold text-charcoal mt-10 mb-4">Evaluaciones</h2>
        <div data-testid={TALENT_MAP.evaluationsSection} className="bg-card border border-petroleum/10 rounded-xl p-5">
          <p className="text-sm text-charcoal/80 mb-1">Periodicidad de evaluaciones de desempeño</p>
          <p className="text-xs text-charcoal/50 mb-3">Define cada cuánto tu empresa hace evaluaciones formales de desempeño. Esto es solo de referencia por ahora, no genera recordatorios todavía.</p>
          <select
            data-testid={TALENT_MAP.evaluationCadenceSelect}
            value={evaluationCadence}
            disabled={savingCadence}
            onChange={(e) => saveEvaluationCadence(e.target.value)}
            className="border border-petroleum/20 rounded-lg px-3 py-2 bg-white text-sm"
          >
            <option value="">Sin configurar</option>
            {EVALUATION_CADENCE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          <div className="mt-5 space-y-2">
            {data?.employees.map((e) => (
              <div key={e.id} data-testid={TALENT_MAP.evaluationRow} className="flex items-center justify-between flex-wrap gap-2 bg-white border border-petroleum/10 rounded-lg p-3">
                <div>
                  <p className="text-sm font-medium text-charcoal">{e.full_name}</p>
                  <p className="text-xs text-charcoal/50">
                    {e.last_evaluation
                      ? `Última evaluación: ${e.last_evaluation.period_label} · promedio ${e.last_evaluation.average_score}/5`
                      : "Sin evaluaciones registradas"}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {e.last_evaluation && (
                    <Button
                      data-testid={TALENT_MAP.evaluationDownloadPdfButton}
                      variant="outline"
                      size="sm"
                      className="rounded-full h-8 text-xs"
                      disabled={downloadingId === e.id}
                      onClick={() => downloadEvaluationPdf(e)}
                    >
                      <FileDown className="w-3.5 h-3.5 mr-1" /> PDF
                    </Button>
                  )}
                  <Button
                    data-testid={TALENT_MAP.evaluateButton}
                    size="sm"
                    className="rounded-full h-8 text-xs bg-terracotta hover:bg-terracotta/90"
                    onClick={() => setEvaluatingEmployee(e)}
                  >
                    <ClipboardCheck className="w-3.5 h-3.5 mr-1" /> Evaluar
                  </Button>
                </div>
              </div>
            ))}
            {data && data.employees.length === 0 && <p className="text-charcoal/50 text-sm">Sin empleados para evaluar.</p>}
          </div>
        </div>

        <EvaluationDialog
          employee={evaluatingEmployee}
          open={!!evaluatingEmployee}
          onOpenChange={(v) => { if (!v) setEvaluatingEmployee(null); }}
          onSaved={() => loadTalentMap()}
        />
      </div>
    </AdminLayout>
  );
}
